import { useState } from 'react';
import { API } from '../config/config';
import { useAuth } from './useAuth';
import { Dayjs } from 'dayjs';
import { CreateDiscountRequest } from 'src/content/applications/Discounts/discounts.interface';

// Existing interfaces
interface VerifyDiscountParams {
  discountCodes: string[];
}

interface DiscountVerificationResult {
  valid: boolean;
  response?: Response;
  error?: string;
}

// New interfaces for discount code CRUD operations
interface CreateDiscountCodeParams {
  prefix: string;
  appliesTo:
    | 'ALL_VENDORS_ALL_PRODUCTS'
    | 'ALL_VENDORS'
    | 'ALL_PRODUCTS'
    | 'SELECTED_PRODUCTS_AND_VENDOR'
    | 'SELECTED_VENDORS_AND_SELECTED_PRODUCTS';
  assignedTo: 'ALL_BUYERS' | 'SELECTED_BUYERS';
  vendors?: any[];
  products?: any[];
  buyers?: any[];
  starts_at: Dayjs;
  ends_at: Dayjs;
  discount_value: number;
}

interface UpdateDiscountCodeParams {
  startsAt: Dayjs;
  endsAt: Dayjs;
}

interface DiscountCodeProduct {
  productId: string;
  productDescription: string;
}

interface DiscountCodeVendor {
  vendorId: string;
  vendorName: string;
}

interface DiscountCodeBuyer {
  buyerId: string;
  buyerEmail: string;
  buyerFullName: string;
}

interface DiscountCode {
  id: string;
  code: string;
  discountType: string;
  discountValue: number;
  appliesTo: string;
  assignedTo: string;
  startsAt: string;
  endsAt: string;
  createdAt: string;
  products: DiscountCodeProduct[];
  vendors: DiscountCodeVendor[];
  buyers: DiscountCodeBuyer[];
  usageCount: number;
}

interface PaginatedDiscountCodes {
  content: DiscountCode[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: string;
  };
  totalElements: number;
  totalPages: number;
  last: boolean;
  first: boolean;
}

interface GetDiscountCodesParams {
  page?: number;
  size?: number;
  sort?: string;
}

export const useDiscountAPI = () => {
  const { enhancedFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationLoading, setVerificationLoading] = useState(false);

  // Verify a single discount code
  const verifyDiscountCode = async (
    params: VerifyDiscountParams
  ): Promise<DiscountVerificationResult | null> => {
    setVerificationLoading(true);
    setError(null);

    try {
      const response = await enhancedFetch(
        `${API.HOST}/api/v1/discount-codes/verify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            discountCodes: params.discountCodes
          })
        }
      );

      return {
        valid: true,
        response: response
      };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to verify discount code';
      setError(errorMessage);
      return {
        valid: false,
        error: errorMessage
      };
    } finally {
      setVerificationLoading(false);
    }
  };

  // Create discount code (POST)
  const createDiscountCode = async (
    params: CreateDiscountCodeParams
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    // Mutate the params before sending

    const request: CreateDiscountRequest = {
      appliesTo: params.appliesTo,
      assignedTo: params.assignedTo,
      prefix: params.prefix,
      startsAt: params.starts_at.toISOString(),
      endsAt: params.ends_at.toISOString(),
      buyers: params?.buyers?.map((item) => ({ email: item })),
      vendors: params?.vendors
        ?.filter((v) => v.hasOwnProperty('vendorId'))
        .map((v) => ({ vendorId: v.vendorId.id })),
      products: params?.products
        ?.filter((p) => p.id)
        .map((p) => ({ productId: p.id })),
      discountValue: params.discount_value
    };

    try {
      await enhancedFetch(`${API.HOST}/api/v1/discount-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });
      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create discount code';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update discount code (PATCH)
  const updateDiscountCode = async (
    id: string,
    params: UpdateDiscountCodeParams
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await enhancedFetch(`${API.HOST}/api/v1/discount-codes/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      });
      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update discount code';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Get paginated discount codes (GET ALL)
  const getDiscountCodes = async (
    params?: GetDiscountCodesParams
  ): Promise<PaginatedDiscountCodes | null> => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      if (params?.page !== undefined)
        queryParams.append('page', params.page.toString());
      if (params?.size !== undefined)
        queryParams.append('size', params.size.toString());
      if (params?.sort) queryParams.append('sort', params.sort);

      const url = `${API.HOST}/api/v1/discount-codes${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;

      const data = await enhancedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return data as PaginatedDiscountCodes;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch discount codes';
      setError(errorMessage);
      setLoading(false);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Get single discount code by ID (GET ONE)
  const getDiscountCodeById = async (
    id: string
  ): Promise<DiscountCode | null> => {
    setLoading(true);
    setError(null);

    try {
      const data = await enhancedFetch(
        `${API.HOST}/api/v1/discount-codes/${id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return data as DiscountCode;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch discount code';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    // Functions
    verifyDiscountCode,
    createDiscountCode,
    updateDiscountCode,
    getDiscountCodes,
    getDiscountCodeById,
    clearError,

    // State
    loading,
    verificationLoading,
    error
  };
};
